import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";

import {
  OrganisationTypeConstants,
  TemplateTaskConstants,
  WorkflowTemplateOrganisationAccessLevel,
} from "../../constants";
import {
  DeadlineCalendarUnit,
  DefaultDeadline,
  SearchWorkflowTemplateTasksResponseTask,
  SelectData,
  TemplateTaskPanelData,
} from "../../models";
import { WorkflowTemplateContext } from "../../route/shared/workflows";
import { GetWorkflowTemplateResponse } from "../../service/query";
import { BaseResponseWithSingle, ServiceError, Status } from "../../service/Shared";
import {
  createWorkflowTemplateTask,
  CreateWorkflowTemplateTaskResponse,
  updateWorkflowTemplateTask,
  UpdateWorkflowTemplateTaskResponse,
} from "../../service/workflow";
import { useAuth } from "../../useAuth";
import { calculateCalanderUnit, useIsLoadingWrapper } from "../../utils";
import { Toast } from "../../widget";
import { getPhaseKeyDatesSelectDataFromDateSchema } from "./utils/getPhaseKeyDatesSelectDataFromSchema";

interface UseWorkflowTemplateTaskPanelProps {
  templateTask?: SearchWorkflowTemplateTasksResponseTask;
  currentStepUuid?: string;
  onClose: () => void;
}

interface UseWorkflowTemplateTaskPanelReturnData {
  errors: ServiceError[] | undefined;
  canEditTask: boolean;
  workflowTemplate: GetWorkflowTemplateResponse | undefined;
  organisationTypeSelectData: SelectData;
  stepSelectData: SelectData;
  phaseKeyDatesSelectData: SelectData;
  currentStepPhase: string | undefined;

  currentTemplateTask: TemplateTaskPanelData;
  setCurrentTemplateTask: Dispatch<SetStateAction<TemplateTaskPanelData>>;

  isTitleEditable: boolean;
  setIsTitleEditable: Dispatch<SetStateAction<boolean>>;

  showDeleteTemplateTaskModal: boolean;
  setShowDeleteTemplateTaskModal: Dispatch<SetStateAction<boolean>>;

  defaultDeadline: DefaultDeadline;
  setDefaultDeadline: Dispatch<SetStateAction<DefaultDeadline>>;

  handleCloseTaskPanel: () => void;
  handleSubmit: () => Promise<void>;
  isHandleSubmitLoading: boolean;
  mustSelectedOrgTypeVisibility: string;
  shouldHideOrganisationFields: boolean;
  isNewTask: boolean;
}

export const useWorkflowTemplateTaskPanel = ({
  templateTask,
  currentStepUuid,
  onClose,
}: UseWorkflowTemplateTaskPanelProps): UseWorkflowTemplateTaskPanelReturnData => {
  const { currentUserType } = useAuth();
  const { canEditStepOrTask, dateSchemas, hasAccessLevelAndCanEditTemplate, workflowTemplate, workflowTemplatePhases } =
    useContext(WorkflowTemplateContext);
  const [errors, setErrors] = useState<ServiceError[]>();
  const { shouldRefreshWorkflowTemplate } = useContext(WorkflowTemplateContext);
  const [canEditTask, setCanEditTask] = useState(false);
  const [isTitleEditable, setIsTitleEditable] = useState(false);
  const [showDeleteTemplateTaskModal, setShowDeleteTemplateTaskModal] = useState(false);

  const [stepSelectData, setStepSelectData] = useState<SelectData>([]);
  const [phaseKeyDatesSelectData, setPhaseKeyDatesSelectData] = useState<SelectData>([]);

  const [currentStepPhase, setCurrentStepPhase] = useState<string>();
  const isProjectWorkflowTemplate = workflowTemplate?.type === "Project";
  const isDealWorkflowTemplate = workflowTemplate?.type === "Deal";
  const organisationTypeSelectData = isDealWorkflowTemplate
    ? ([
        {
          key: TemplateTaskConstants.ORGANISATION_TYPE_SELECT_DATA_KEYS[OrganisationTypeConstants.ASSET_MANAGER],
          value: "Asset Manager",
        },
      ] as SelectData)
    : TemplateTaskConstants.ORGANISATION_TYPE_SELECT_DATA;
  let mustSelectedOrgTypeVisibility = TemplateTaskConstants.ORGANISATION_TYPE_SELECT_DATA_KEYS.Developer;
  let defaultOwnerOrganisationType = TemplateTaskConstants.ORGANISATION_TYPE_SELECT_DATA_KEYS.Developer;

  if (currentUserType === OrganisationTypeConstants.ASSET_MANAGER) {
    mustSelectedOrgTypeVisibility = TemplateTaskConstants.ORGANISATION_TYPE_SELECT_DATA_KEYS.AssetManager;
    defaultOwnerOrganisationType = TemplateTaskConstants.ORGANISATION_TYPE_SELECT_DATA_KEYS.AssetManager;
  } else if (currentUserType === OrganisationTypeConstants.DEVELOPMENT_MANAGER) {
    mustSelectedOrgTypeVisibility = TemplateTaskConstants.ORGANISATION_TYPE_SELECT_DATA_KEYS.DevelopmentManager;
    defaultOwnerOrganisationType = TemplateTaskConstants.ORGANISATION_TYPE_SELECT_DATA_KEYS.DevelopmentManager;
  }

  const [currentTemplateTask, setCurrentTemplateTask] = useState<TemplateTaskPanelData>(
    templateTask === undefined
      ? {
          ...TemplateTaskConstants.DEFAULT_TEMPLATE_TASK,
          defaultVisibility: [mustSelectedOrgTypeVisibility],
          ownerOrganisationType: defaultOwnerOrganisationType,
        } // For create, use default values
      : {
          defaultDeadlineField: templateTask.defaultDeadlineField,
          defaultDeadlineValue: templateTask.defaultDeadlineValue,
          defaultIsMandatory: templateTask.defaultIsMandatory,
          defaultIsMilestone: templateTask.defaultIsMilestone,
          // NOTE - hiding this for now KAN-7325 - https://kanaearth.atlassian.net/browse/KAN-7325
          defaultKeyDate: templateTask.defaultKeyDate,
          defaultRepeat: templateTask.defaultRepeat,
          defaultTaskDescription: templateTask.defaultDescription,
          defaultTaskName: templateTask.defaultName,
          defaultVisibility: templateTask.defaultVisibility,
          ownerOrganisationType: templateTask.ownerOrganisationType,
          workflowTemplateStepUuid: currentStepUuid ?? null,
        }
  );

  const convertNumberToDefaultDeadline = (number: number): DefaultDeadline => {
    const operator = number >= 0 ? "+" : "-";
    const value = Math.abs(number);
    const calendarUnit = calculateCalanderUnit(number) as DeadlineCalendarUnit;
    return {
      operator,
      value: value / TemplateTaskConstants.DEADLINE_CALENDAR_UNIT_MULTIPLIERS[calendarUnit],
      unit: calendarUnit,
    };
  };

  const [defaultDeadline, setDefaultDeadline] = useState<DefaultDeadline>(
    currentTemplateTask.defaultDeadlineValue !== null
      ? convertNumberToDefaultDeadline(currentTemplateTask.defaultDeadlineValue)
      : TemplateTaskConstants.DEFAULT_DEADLINE
  );
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  const resetTemplateTaskPanel = (): void => {
    setIsTitleEditable(false);
    setCanEditTask(false);
    setCurrentStepPhase(undefined);
    setCurrentTemplateTask(TemplateTaskConstants.DEFAULT_TEMPLATE_TASK);
    setDefaultDeadline(TemplateTaskConstants.DEFAULT_DEADLINE);
  };

  const handleCloseTaskPanel = (): void => {
    resetTemplateTaskPanel();
    onClose();
  };

  const handleSubmit = useIsLoadingWrapper(async (): Promise<void> => {
    setErrors([]);

    // Always compute the latest defaultDeadlineValue from defaultDeadline before submit
    let computedDeadlineValue = currentTemplateTask.defaultDeadlineValue;
    if (defaultDeadline && defaultDeadline.unit) {
      const multiplier = TemplateTaskConstants.DEADLINE_CALENDAR_UNIT_MULTIPLIERS[defaultDeadline.unit];
      const value = defaultDeadline.value * multiplier;
      computedDeadlineValue = defaultDeadline.operator === "+" ? value : -value;
    }

    let res: BaseResponseWithSingle<UpdateWorkflowTemplateTaskResponse | CreateWorkflowTemplateTaskResponse>;
    const ownerOrganisationType = isDealWorkflowTemplate
      ? TemplateTaskConstants.ORGANISATION_TYPE_SELECT_DATA_KEYS.AssetManager
      : (currentTemplateTask.ownerOrganisationType ?? "");
    const defaultVisibility = isDealWorkflowTemplate
      ? [TemplateTaskConstants.ORGANISATION_TYPE_SELECT_DATA_KEYS.AssetManager]
      : (currentTemplateTask.defaultVisibility ?? []);
    if (templateTask) {
      // UpdateWorkflowTemplate
      res = await updateWorkflowTemplateTask({
        workflowTemplateTaskUuid: templateTask.uuid,
        rowVersion: templateTask.rowVersion,
        workflowTemplateStepUuid: currentTemplateTask.workflowTemplateStepUuid,
        defaultTaskName: currentTemplateTask.defaultTaskName,
        defaultTaskDescription: currentTemplateTask.defaultTaskDescription,
        defaultDeadlineValue: computedDeadlineValue,
        defaultDeadlineField: currentTemplateTask.defaultDeadlineField,
        defaultIsMandatory: currentTemplateTask.defaultIsMandatory,
        defaultIsMilestone: currentTemplateTask.defaultIsMilestone,
        defaultKeyDate: currentTemplateTask.defaultKeyDate,
        defaultRepeat: currentTemplateTask.defaultRepeat,
        defaultVisibility,
        ownerOrganisationType,
      });
    } else {
      // CreateWorkflowTemplate
      res = await createWorkflowTemplateTask({
        workflowTemplateStepUuid: currentTemplateTask.workflowTemplateStepUuid ?? "",
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        defaultTaskName: currentTemplateTask.defaultTaskName!,
        defaultTaskDescription: currentTemplateTask.defaultTaskDescription,
        defaultDeadlineValue: computedDeadlineValue ?? 0,
        defaultDeadlineField: currentTemplateTask.defaultDeadlineField ?? "",
        defaultIsMandatory: currentTemplateTask.defaultIsMandatory ?? false,
        defaultIsMilestone: currentTemplateTask.defaultIsMilestone ?? false,
        defaultKeyDate: currentTemplateTask.defaultKeyDate ?? "",
        defaultRepeat: currentTemplateTask.defaultRepeat ?? false,
        defaultVisibility,
        ownerOrganisationType,
      });
    }
    if (res.status === Status.Success && res.data) {
      Toast.success({
        message: `Workflow template task ${templateTask ? "updated" : "created"} successfully`,
      });
      handleCloseTaskPanel();
      shouldRefreshWorkflowTemplate();
    }

    if (res.status === Status.Error) {
      setErrors(res.errors);
    }
  }, setIsHandleSubmitLoading);

  // Checks if user can edit the opened task / if creating new task, has Contributor permission
  useEffect(() => {
    if (templateTask?.createdByOrganisationUuid) {
      canEditStepOrTask(templateTask.createdByOrganisationUuid).then((canEdit) => setCanEditTask(canEdit));
    } else {
      setCanEditTask(hasAccessLevelAndCanEditTemplate(WorkflowTemplateOrganisationAccessLevel.Contributor));
    }
  }, [templateTask?.createdByOrganisationUuid]);

  // Updates current step phase & phase key dates for dropdown
  useEffect(() => {
    const phase = currentTemplateTask?.workflowTemplateStepUuid
      ? workflowTemplate?.steps.find((step) => step.uuid === currentTemplateTask?.workflowTemplateStepUuid)
          ?.defaultPhase
      : templateTask?.defaultStep.phase;
    const selectedPhase = workflowTemplatePhases.find((phaseItem) => phaseItem.phase === phase);

    setCurrentStepPhase(phase);
    if (!phase) {
      setPhaseKeyDatesSelectData([]);
      return;
    }

    const schemaDrivenSelectData = getPhaseKeyDatesSelectDataFromDateSchema(
      phase,
      dateSchemas,
      selectedPhase?.keyDates
    );
    setPhaseKeyDatesSelectData(
      schemaDrivenSelectData.length > 0
        ? schemaDrivenSelectData
        : TemplateTaskConstants.PHASE_KEY_DATES_SELECT_DATA_MAP[
            phase as keyof typeof TemplateTaskConstants.PHASE_KEY_DATES_SELECT_DATA_MAP
          ]
    );
  }, [
    currentTemplateTask.workflowTemplateStepUuid,
    dateSchemas,
    templateTask?.defaultStep.phase,
    workflowTemplatePhases,
    workflowTemplate?.steps,
  ]);

  // Set default values for create new task panel
  useEffect(() => {
    if (currentStepUuid) {
      setCurrentTemplateTask({
        ...currentTemplateTask,
        workflowTemplateStepUuid: currentStepUuid,
      });
    }
  }, [currentStepUuid]);

  useEffect(() => {
    if (!isDealWorkflowTemplate) {
      return;
    }

    setCurrentTemplateTask((prevTask) => ({
      ...prevTask,
      defaultVisibility: [TemplateTaskConstants.ORGANISATION_TYPE_SELECT_DATA_KEYS.AssetManager],
      ownerOrganisationType: TemplateTaskConstants.ORGANISATION_TYPE_SELECT_DATA_KEYS.AssetManager,
    }));
  }, [isDealWorkflowTemplate]);

  // Updates step select data from workflow tamplate
  useDeepCompareEffect(() => {
    if (workflowTemplate?.steps && workflowTemplate?.steps?.length > 0) {
      setStepSelectData(
        workflowTemplate?.steps.map((step) => ({ key: step.uuid, value: step.defaultStepName })) as SelectData
      );
    }
  }, [workflowTemplate?.steps]);

  return {
    errors,
    canEditTask,
    workflowTemplate,
    organisationTypeSelectData,
    currentStepPhase,
    phaseKeyDatesSelectData,
    stepSelectData,

    currentTemplateTask,
    setCurrentTemplateTask,

    isTitleEditable,
    setIsTitleEditable,

    showDeleteTemplateTaskModal,
    setShowDeleteTemplateTaskModal,

    defaultDeadline,
    setDefaultDeadline,

    handleCloseTaskPanel,
    handleSubmit,
    isHandleSubmitLoading,
    mustSelectedOrgTypeVisibility,
    shouldHideOrganisationFields: !isProjectWorkflowTemplate,
    isNewTask: templateTask === undefined,
  };
};
